import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormGroupDirective, FormsModule } from '@angular/forms';
import { MobilePaymentType } from '@app/sales/enums/mobile-payment-type';
import { SubscriptionForMobilbytteDTO } from '@app/sales/modules/mobilephone/models/mobilephone-variant';
import { VoiceProduct } from '@app/shared/models/voice-product.model';
import { PriceType } from '@app/shared/pipes/price-format.pipe';
import { MobilePhoneFormControls } from '../../mobilephone-details.component';
import { ErrorStateOnTouched } from '@app/material/ErrorStateOnTouched';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import { VoiceSubscriptionOffering } from '@app/sales/modules/subscription/models/offerings-response.model';
import { Subscription } from 'rxjs';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { MatOptgroup, MatOption } from '@angular/material/core';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatFormField } from '@angular/material/form-field';

@Component({
  selector: 'app-mobilbytte-offering-selector',
  templateUrl: './mobilbytte-offering-selector.component.html',
  styleUrl: './mobilbytte-offering-selector.component.scss',
  imports: [MatFormField, MatSelect, FormsModule, MatSelectTrigger, MatTooltip, MatOptgroup, MatOption, PriceFormatPipe],
})
export class MobilbytteOfferingSelectorComponent implements OnInit, OnDestroy {
  formGroupDirective = inject(FormGroupDirective);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input({ required: true }) availableSubscriptions: SubscriptionForMobilbytteDTO[];
  @Input({ required: true }) allVoiceOfferings: VoiceSubscriptionOffering[];
  @Input({ required: true }) existingProductsAvailableForEquipmentUpgrade: VoiceProduct[];
  @Input({ required: true }) allVoiceProducts: VoiceProduct[];
  @Output() equipmentUpgradeNewOfferingChanged = new EventEmitter();

  MobilePaymentType = MobilePaymentType;
  PriceType = PriceType;
  MobilePhoneFormControls = MobilePhoneFormControls;
  matcher = new ErrorStateOnTouched();

  existingProductsNotAvailableForEquipmentUpgrade: VoiceProduct[];
  selectedOptionId: string = null;
  selectedNewOffering: VoiceSubscriptionOffering;
  existingVoiceProduct: VoiceProduct;
  subscriptions$: Subscription[] = [];

  getVasProductFromOffering = OfferingHelperService.getVasProductFromOffering;
  getMobilePlanProductFromOffering = OfferingHelperService.getMobilePlanProductFromOffering;
  equipmentUpgradeCompatibleOfferings: VoiceSubscriptionOffering[];

  ngOnInit(): void {
    this.existingProductsNotAvailableForEquipmentUpgrade = this.allVoiceProducts.filter(
      voiceProduct =>
        voiceProduct.isExistingSubscription &&
        !this.existingProductsAvailableForEquipmentUpgrade.map(available => available.id).includes(voiceProduct.id)
    );

    this.subscriptions$.push(
      this.formGroupDirective.form.get(MobilePhoneFormControls.MobilBytteOffering)?.valueChanges.subscribe(() => {
        this.selectedNewOffering = null;
        this.selectedOptionId = null;
        this.changeDetectorRef.detectChanges();
      })
    );

    // Filtered offering should be the ones in allVoiceOfferings that is in the availableSubscriptions
    const filteredOfferings = this.allVoiceOfferings.filter(offering =>
      this.availableSubscriptions.some(sub => sub.subscriptionOfferingId === offering.id)
    );
    this.equipmentUpgradeCompatibleOfferings = OfferingHelperService.sortOfferingsByMobilePlanPriceAsc(filteredOfferings);

    if (
      this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).value &&
      !this.formGroupDirective.form.get(MobilePhoneFormControls.ExistingSubscriptionId).value
    ) {
      this.selectedOptionId = this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).value;
      this.onSelectedOfferingChanged();
    } else if (
      !this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).value &&
      this.formGroupDirective.form.get(MobilePhoneFormControls.ExistingSubscriptionId).value
    ) {
      this.selectedOptionId = this.formGroupDirective.form.get(MobilePhoneFormControls.ExistingSubscriptionId).value;
      this.onSelectedOfferingChanged();
    }
  }

  onSelectedOfferingChanged() {
    const isExisting = !this.availableSubscriptions.some(o => o.subscriptionOfferingId === this.selectedOptionId);

    if (isExisting) {
      this.selectedNewOffering = null;
      this.existingVoiceProduct = this.existingProductsAvailableForEquipmentUpgrade.find(
        offering => offering.id === this.selectedOptionId
      );
      this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).setValue(null);
      this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).disable();
      this.formGroupDirective.form.get(MobilePhoneFormControls.ExistingSubscriptionId).enable();
      this.formGroupDirective.form.get(MobilePhoneFormControls.ExistingSubscriptionId).setValue(this.selectedOptionId);
      this.equipmentUpgradeNewOfferingChanged.emit(null);
    } else {
      this.existingVoiceProduct = null;
      this.selectedNewOffering = this.getVoiceSubscriptionOffering(this.selectedOptionId);
      this.formGroupDirective.form.get(MobilePhoneFormControls.ExistingSubscriptionId).setValue(null);
      this.formGroupDirective.form.get(MobilePhoneFormControls.ExistingSubscriptionId).disable();
      this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).enable();
      this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).setValue(this.selectedOptionId);
      this.equipmentUpgradeNewOfferingChanged.emit(this.selectedNewOffering?.id);
    }
    this.changeDetectorRef.detectChanges();
  }

  private getVoiceSubscriptionOffering(offeringId: string): VoiceSubscriptionOffering {
    return this.allVoiceOfferings.find(o => o.id === offeringId);
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
